import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import {
  formatGateClosureInResourceTz,
  getResourceTimezoneLabel,
  getResourceTimezoneLabels,
} from '../../../utils/datetime';

const countLabel = (n, singular, plural) => `${n} ${n === 1 ? singular : plural}`;

// Rows that fit in the scroll area before it overflows (max-h-36 ≈ 5 rows);
// above this we show a fade hint that the list scrolls.
const VISIBLE_GATES = 5;

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-3">
    <dt className="text-gray-500 dark:text-gray-400">{label}</dt>
    <dd className="font-semibold text-gray-900 dark:text-gray-100">{value}</dd>
  </div>
);

const SectionLabel = ({ children }) => (
  <div className="text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1.5">
    {children}
  </div>
);

// One clickable gate row inside a horizon group. Shows an open/closed status
// dot, the gate's closure date + time, and highlights when selected. The row's
// own CET/CEST is visible when the day's gates straddle a DST switch (`showTz`)
// and screen-reader only otherwise, since the header isn't tied to the button.
const GateRow = ({ gate, selected, onSelect, showTz }) => {
  const closure = formatGateClosureInResourceTz(gate.gateClosure);
  const date = closure?.date || null;
  const time = closure?.time || '—';
  const tz = closure ? getResourceTimezoneLabel(undefined, new Date(gate.gateClosure)) : null;
  return (
    <button
      type="button"
      onClick={() => onSelect(gate.challengeId)}
      aria-pressed={selected}
      className={`flex w-full items-center gap-2 rounded px-2 py-1 text-xs transition-colors ${
        selected
          ? 'bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300 font-semibold'
          : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
      }`}
    >
      <span
        title={gate.isOpen ? 'Open' : 'Closed'}
        className={`h-1.5 w-1.5 flex-shrink-0 rounded-full ${
          gate.isOpen ? 'bg-emerald-500' : 'bg-gray-300 dark:bg-gray-600'
        }`}
      />
      {date && <span className="font-medium">{date}</span>}
      <span className="text-gray-400 dark:text-gray-500">·</span>
      <span>{time}</span>
      {tz && <span className={showTz ? 'text-gray-400 dark:text-gray-500' : 'sr-only'}>{tz}</span>}
    </button>
  );
};

// Left-column summary of the selected target day: quick stats, a per-horizon
// gate breakdown (clickable — keeps the chart in sync), participation for the
// selected gate, and detail of the active gate. Gate times are in the resource
// timezone, labelled in the section header (CET/CEST) and per row on DST days.
export const DayInfoPanel = ({
  info,
  horizonGroups = [],
  selectedChallengeId,
  onSelectGate,
  participation = null,
}) => {
  // Accordion: one horizon group open at a time. Defaults to (and follows) the
  // group that holds the selected gate, so picking a gate via the top filter
  // chip unfolds the matching horizon here.
  const selectedGroupKey =
    horizonGroups.find((g) => g.gates.some((gate) => gate.challengeId === selectedChallengeId))?.key ||
    null;
  const [openKey, setOpenKey] = useState(selectedGroupKey);
  // A day's gates span D-2..D and can straddle a DST switch: one label goes in
  // the header when they agree, otherwise every row carries its own.
  const tzLabels = getResourceTimezoneLabels(
    horizonGroups.flatMap((g) => g.gates.map((gate) => gate.gateClosure))
  );
  const labelRows = tzLabels.length > 1;
  useEffect(() => {
    if (selectedGroupKey) setOpenKey(selectedGroupKey);
  }, [selectedGroupKey]);

  // Hide the scroll fade once the open list is scrolled to the bottom.
  const [atBottom, setAtBottom] = useState(false);
  useEffect(() => setAtBottom(false), [openKey]);
  const onGatesScroll = (event) => {
    const el = event.currentTarget;
    setAtBottom(el.scrollTop + el.clientHeight >= el.scrollHeight - 4);
  };

  if (!info?.targetDay) {
    return (
      <div className="h-full bg-surface dark:bg-gray-870 rounded-lg p-4 border border-gray-200/70 dark:border-gray-600/70 shadow-sm text-sm text-gray-500 dark:text-gray-400">
        Select a day to see its details.
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col bg-surface dark:bg-gray-870 rounded-lg p-4 border border-gray-200/70 dark:border-gray-600/70 shadow-sm">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
        This target day
      </div>
      <div className="mt-1 text-lg font-bold text-gray-900 dark:text-gray-100">
        {moment(info.targetDay).format('ddd, MMM DD, YYYY')}
      </div>

      <dl className="mt-3 space-y-1.5 text-sm">
        <Row label="Sessions" value={countLabel(info.sessionCount, 'session', 'sessions')} />
        {info.useCase && <Row label="Use case" value={info.useCase} />}
      </dl>

      {/* Per-horizon gate breakdown — the navigable map of the day. Flexes to
          fill the card and scrolls internally, so the panel keeps a constant
          height regardless of how many horizons/gates the day has. */}
      {horizonGroups.length > 0 && (
        <div className="mt-4 flex min-h-0 flex-1 flex-col">
          <SectionLabel>
            Horizons &amp; gates{tzLabels.length > 0 && ` · times in ${tzLabels.join('/')}`}
          </SectionLabel>
          <div className="min-h-0 flex-1 space-y-0.5 overflow-y-auto pr-1">
            {horizonGroups.map((group) => {
              const expanded = openKey === group.key;
              return (
                <div key={group.key}>
                  <button
                    type="button"
                    onClick={() => setOpenKey(expanded ? null : group.key)}
                    aria-expanded={expanded}
                    className="flex w-full items-center gap-1.5 rounded px-1 py-1.5 text-left hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <ChevronRightIcon
                      className={`h-3.5 w-3.5 flex-shrink-0 text-gray-400 transition-transform ${
                        expanded ? 'rotate-90' : ''
                      }`}
                    />
                    <span className="text-xs font-semibold text-gray-700 dark:text-gray-200">
                      {group.label}
                    </span>
                    <span className="ml-auto text-[10px] text-gray-400 dark:text-gray-500">
                      {countLabel(group.gates.length, 'gate', 'gates')}
                    </span>
                  </button>
                  {expanded && (
                    <div className="relative ml-3 mt-0.5">
                      <div
                        onScroll={onGatesScroll}
                        className="max-h-36 space-y-0.5 overflow-y-auto border-l border-gray-100 dark:border-gray-700 pl-2 pr-1"
                      >
                        {group.gates.map((gate) => (
                          <GateRow
                            key={gate.challengeId}
                            gate={gate}
                            selected={gate.challengeId === selectedChallengeId}
                            onSelect={onSelectGate}
                            showTz={labelRows}
                          />
                        ))}
                      </div>
                      {/* Fade hint that the list scrolls (more gates below). */}
                      {group.gates.length > VISIBLE_GATES && !atBottom && (
                        <div className="pointer-events-none absolute inset-x-0 bottom-0 flex h-7 items-end justify-center rounded-b bg-gradient-to-t from-surface dark:from-gray-870">
                          <ChevronDownIcon className="h-3.5 w-3.5 animate-bounce text-gray-400" />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Participation for the selected gate (market-owner / admin only). */}
      {participation && (
        <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-700">
          <SectionLabel>Participation</SectionLabel>
          <dl className="space-y-1.5 text-sm">
            <Row label="Forecasters" value={participation.forecasterCount} />
            <Row label="Ensemble" value={participation.hasEnsemble ? 'Yes' : 'No'} />
          </dl>
        </div>
      )}
    </div>
  );
};
